import React, { useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { 
  Autocomplete, 
  TextField, 
  TextFieldProps, 
  Checkbox, 
  SxProps,
  Divider,
  useTheme
} from "@mui/material";
import { 
  CheckBoxOutlineBlank, 
  CheckBox,
  RadioButtonUnchecked,
  RadioButtonChecked,
  Add
} from "@mui/icons-material";

interface RHFAutoCompleteStringProps {
  name: string;
  label: string;
  options: string[] | (() => Promise<string[]>);
  createNew?: boolean;
  multiple?: boolean;
  required?: boolean;
  defaultValue?: string | string[];
  disabled?: boolean;
  fullWidth?: boolean;
  size?: TextFieldProps['size'];
  sx?: SxProps;
  onOptionChange?: (value: any) => void;
  instanceId?: string;
  selectAll?: boolean;
  onCreateNew?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const RHFAutoCompleteString: React.FC<RHFAutoCompleteStringProps> = ({
  name,
  label,
  options,
  createNew = true,
  required = false,
  multiple = false,
  defaultValue = multiple ? [] : '',
  disabled = false,
  fullWidth = true,
  size = "medium",
  sx,
  onOptionChange,
  instanceId,
  selectAll = false,
  onCreateNew,
  onBlur,
}) => {

  const { control, formState: { errors } } = useFormContext();
  const error = errors[name];
  const [autoOptions, setAutoOptions] = useState<string[]>([]);
  const [displayOptions, setDisplayOptions] = useState<string[]>([]);
  const SELECT_ALL_OPTION = 'Select All';
  const CREATE_NEW_OPTION = 'Create New';
  const theme = useTheme();

  useEffect(() => {
    const processOptions = async () => {
      let processedOptions: string[];
      if (typeof options === "function") {
        processedOptions = await options().then(res => 
          Array.isArray(res) ? res.map(String) : []
        );
      } else {
        processedOptions = Array.isArray(options) ? options.map(String) : [];
      }
      
      setAutoOptions(processedOptions);
      
      let finalOptions = processedOptions;
      
      // Add Select All option if needed
      if (multiple && selectAll && processedOptions.length > 0) {
        finalOptions = [SELECT_ALL_OPTION, ...finalOptions];
      }
      
      // Add Create New option if onCreateNew is provided
      if (onCreateNew) {
        finalOptions = [CREATE_NEW_OPTION, ...finalOptions];
      }
      
      setDisplayOptions(finalOptions);
    };

    processOptions();
  }, [options, multiple, selectAll, onCreateNew]);

  // Function to handle Select All toggle
  const handleSelectAllToggle = (field: any, allSelected: boolean) => {
    if (allSelected) {
      // If all are selected, deselect all
      field.onChange([]);
      onOptionChange?.([]);
    } else {
      // If not all selected, select all actual options
      field.onChange([...autoOptions]);
      onOptionChange?.([...autoOptions]);
    }
  };

  return (
    <Controller
      key={instanceId}
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{ 
        required: required 
          ? { value: true, message: `${label} is required` } 
          : false 
      }}
      render={({ field }) => {
        // Check if all options are selected
        const allSelected = Array.isArray(field.value) && autoOptions.length > 0 && 
          autoOptions.every(opt => Array.isArray(field.value) && field.value.includes(opt));
        
        return (
          <Autocomplete
            key={instanceId}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: size === "small" ? 38 : 54,
                height: 'auto',
                flexWrap: 'wrap',
              },
              ...sx
            }}
            size={size}
            multiple={multiple}
            freeSolo={createNew}
            options={displayOptions}
            disabled={disabled}
            value={multiple ? (Array.isArray(field.value) ? field.value : defaultValue || []) : field.value || defaultValue || ""}
            onChange={(_, newValue) => {
              // Check if Create New was clicked
              if (!multiple && newValue === CREATE_NEW_OPTION && onCreateNew) {
                // Reset the field to previous value and call onCreateNew
                onCreateNew();
                return;
              }
              
              if (multiple && Array.isArray(newValue) && newValue.includes(CREATE_NEW_OPTION) && onCreateNew) {
                // Remove CREATE_NEW_OPTION from selection
                onCreateNew();
                return;
              }
              
              if (multiple) {
                // Check if Select All was clicked
                if (Array.isArray(newValue)) {
                  const hadSelectAll = Array.isArray(field.value) && field.value.includes(SELECT_ALL_OPTION);
                  const hasSelectAll = newValue.includes(SELECT_ALL_OPTION);
                  
                  if (hasSelectAll !== hadSelectAll) {
                    // Select All option was toggled
                    handleSelectAllToggle(field, allSelected);
                    return;
                  }
                  
                  // Filter out the special options from the actual value
                  const filteredValue = newValue.filter(item => 
                    item !== SELECT_ALL_OPTION && item !== CREATE_NEW_OPTION
                  );
                  field.onChange(filteredValue);
                  onOptionChange?.(filteredValue);
                  return;
                }
                
                field.onChange(Array.isArray(newValue) ? newValue.map(String) : []);
              } else {
                field.onChange(newValue ? String(newValue) : '');
              }
              onOptionChange?.(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={!!error}
                //   helperText={error?.message}
                variant="outlined"
                size={size}
                fullWidth={fullWidth}
                required={required}
                onBlur={onBlur}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    autoComplete: "no-name",
                  },
                  inputLabel: {
                    sx: {
                      '& .MuiFormLabel-asterisk': {
                        color: 'error.main',
                        fontSize: '24px',
                      },
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              // Special handling for Create New option
              if (option === CREATE_NEW_OPTION && onCreateNew) {
                return (
                  <React.Fragment key={option}>
                    <li 
                      {...props} 
                      onClick={(e) => {
                        e.preventDefault();
                        onCreateNew();
                      }}
                      style={{ 
												color: theme.palette.mode === 'dark' ? 'grey.200' : 'grey.900',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Add sx={{ mr: 1 }} />
                      {option}
                    </li>
                    <Divider />
                  </React.Fragment>
                );
              }
              
              // Special handling for Select All option
              if (option === SELECT_ALL_OPTION && multiple && selectAll) {
                return (
                  <React.Fragment key={option}>
                    <li 
                      {...props} 
                      onClick={(e) => {
                        // Prevent default to handle the click manually
                        e.preventDefault();
                        handleSelectAllToggle(field, allSelected);
                      }}
                    >
                      <Checkbox
                        icon={<CheckBoxOutlineBlank />}
                        checkedIcon={<CheckBox />}
                        checked={allSelected}
                        indeterminate={!allSelected && Array.isArray(field.value) && field.value.length > 0}
                        onChange={() => handleSelectAllToggle(field, allSelected)}
                      />
                      {option}
                    </li>
                    <Divider />
                  </React.Fragment>
                );
              }
              
              return (
                <li {...props} key={option}>
                  {multiple ? (
                    <Checkbox
                      icon={<CheckBoxOutlineBlank />}
                      checkedIcon={<CheckBox />}
                      checked={selected}
                    />
                  ) : (
                    <Checkbox
                      icon={<RadioButtonUnchecked />}
                      checkedIcon={<RadioButtonChecked />}
                      checked={selected}
                    />
                  )}
                  {option}
                </li>
              );
            }}
            disableCloseOnSelect={multiple}
            filterOptions={(options, { inputValue }) => {
              let filteredOptions = options;
              
              // Always keep Create New at the top if it exists
              const createNewOption = onCreateNew ? [CREATE_NEW_OPTION] : [];
              
              // Keep Select All after Create New if applicable
              const selectAllOption = multiple && selectAll && options.includes(SELECT_ALL_OPTION) ? [SELECT_ALL_OPTION] : [];
              
              // Filter the regular options
              const regularOptions = options.filter(opt => 
                opt !== CREATE_NEW_OPTION && opt !== SELECT_ALL_OPTION
              ).filter(
                (option) => option.toLowerCase().includes(inputValue.toLowerCase())
              );
              
              if (inputValue) {
                // When there's input, filter accordingly but keep special options if they match
                const matchesCreateNew = CREATE_NEW_OPTION.toLowerCase().includes(inputValue.toLowerCase());
                const matchesSelectAll = SELECT_ALL_OPTION.toLowerCase().includes(inputValue.toLowerCase());
                
                filteredOptions = [
                  ...(onCreateNew && matchesCreateNew ? createNewOption : []),
                  ...(multiple && selectAll && matchesSelectAll ? selectAllOption : []),
                  ...regularOptions
                ];
              } else {
                // When there's no input, show all options with special ones at the top
                filteredOptions = [
                  ...createNewOption,
                  ...selectAllOption,
                  ...regularOptions
                ];
              }
              
              return createNew && inputValue && 
                !filteredOptions.some(
                  (option) => option.toLowerCase() === inputValue.toLowerCase()
                ) && 
                inputValue !== SELECT_ALL_OPTION && 
                inputValue !== CREATE_NEW_OPTION
                ? [...filteredOptions, inputValue]
                : filteredOptions;
            }}
          />
        );
      }}
    />
  );
};

export default RHFAutoCompleteString;